/**
 * Navigation History Service
 *
 * Tracks internal navigation within VerdeKai to enable proper back/forward
 * button functionality without logging users out.
 *
 * Works by:
 * 1. Maintaining an internal history stack of app states
 * 2. Using browser's History API (pushState) to integrate with back/forward buttons
 * 3. Providing methods to push new states and navigate back/forward
 */

class NavigationHistoryService {
  constructor() {
    this.history = [];
    this.currentIndex = -1;
    this.maxHistory = 50;
    this.isRestoring = false; // Flag to prevent circular updates during restoration
  }

  /**
   * Push a new navigation state onto the history stack
   * @param {Object} state - The state to push (activeView, selectedCase, etc.)
   */
  push(state) {
    // Don't push if we're currently restoring from a popstate
    if (this.isRestoring) return;

    // Don't push if state is identical to current
    const current = this.getCurrent();
    if (current && this.areStatesEqual(current, state)) {
      return;
    }

    // Remove any forward history when pushing new state
    this.history = this.history.slice(0, this.currentIndex + 1);

    // Add new state with timestamp
    this.history.push({
      ...state,
      timestamp: Date.now()
    });

    // Trim if exceeds max
    if (this.history.length > this.maxHistory) {
      this.history.shift();
    } else {
      this.currentIndex++;
    }

    // Update browser history state (only if we have a valid index)
    if (this.currentIndex >= 0) {
      window.history.pushState(
        { vdkIndex: this.currentIndex, vdkNavigation: true },
        ''
      );
    }
  }

  /**
   * Replace the current state without adding to history
   * Useful for initial state setup
   * @param {Object} state - The state to set as current
   */
  replace(state) {
    if (this.currentIndex >= 0) {
      this.history[this.currentIndex] = {
        ...state,
        timestamp: Date.now()
      };
    } else {
      // No history yet, push as first entry
      this.history.push({
        ...state,
        timestamp: Date.now()
      });
      this.currentIndex = 0;
    }

    // Replace browser history state
    window.history.replaceState(
      { vdkIndex: this.currentIndex, vdkNavigation: true },
      ''
    );
  }

  /**
   * Check if we can navigate backward
   * @returns {boolean}
   */
  canGoBack() {
    return this.currentIndex > 0;
  }

  /**
   * Check if we can navigate forward
   * @returns {boolean}
   */
  canGoForward() {
    return this.currentIndex < this.history.length - 1;
  }

  /**
   * Navigate to previous state
   * @returns {Object|null} The previous state, or null if at beginning
   */
  goBack() {
    if (this.canGoBack()) {
      this.currentIndex--;
      return this.history[this.currentIndex];
    }
    return null;
  }

  /**
   * Navigate to next state
   * @returns {Object|null} The next state, or null if at end
   */
  goForward() {
    if (this.canGoForward()) {
      this.currentIndex++;
      return this.history[this.currentIndex];
    }
    return null;
  }

  /**
   * Get the current state
   * @returns {Object|null}
   */
  getCurrent() {
    return this.history[this.currentIndex] || null;
  }

  /**
   * Restore to a specific index (called from popstate handler)
   * @param {number} index - The history index to restore to
   * @returns {Object|null} The state at that index
   */
  restoreToIndex(index) {
    if (index >= 0 && index < this.history.length) {
      this.currentIndex = index;
      return this.history[index];
    }
    return null;
  }

  /**
   * Set the restoring flag (to prevent circular updates)
   * @param {boolean} value
   */
  setRestoring(value) {
    this.isRestoring = value;
  }

  /**
   * Check if two states are equal (to prevent duplicate pushes)
   * @param {Object} state1
   * @param {Object} state2
   * @returns {boolean}
   */
  areStatesEqual(state1, state2) {
    if (!state1 || !state2) return false;

    // Compare key navigation properties
    return (
      state1.activeView === state2.activeView &&
      state1.selectedCase?.id === state2.selectedCase?.id &&
      state1.selectedDocument?.id === state2.selectedDocument?.id &&
      state1.activePanelId === state2.activePanelId
    );
  }

  /**
   * Clear all history (useful for logout)
   */
  clear() {
    this.history = [];
    this.currentIndex = -1;
  }

  /**
   * Get debug info about current state
   * @returns {Object}
   */
  getDebugInfo() {
    return {
      historyLength: this.history.length,
      currentIndex: this.currentIndex,
      canGoBack: this.canGoBack(),
      canGoForward: this.canGoForward(),
      current: this.getCurrent()
    };
  }
}

// Export singleton instance
export const navigationHistoryService = new NavigationHistoryService();
export default navigationHistoryService;
